import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import PagesOutlinedIcon from "@mui/icons-material/PagesOutlined"
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined"
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import Divider from "@mui/material/Divider"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import Column from "../../../../common/components/Column"
import CandidateAvatar from "../CandidateAvatar"
import {
  SEARCH_BY_SEE_ALL_LABELS,
  SEARCH_TYPE_COLORS,
  SEARCH_TYPE_LABELS,
} from "./data"
import { highlightMatch } from "./highlight"

const TYPE_ICONS = {
  candidate: PersonOutlinedIcon,
  lead: PersonSearchOutlinedIcon,
  offer: PagesOutlinedIcon,
  email: EmailOutlinedIcon,
  sms: SmsOutlinedIcon,
  sms_template: SmsOutlinedIcon,
  meeting: GroupsOutlinedIcon,
  call: PhoneOutlinedIcon,
  task: AssignmentOutlinedIcon,
  workflow: AccountTreeOutlinedIcon,
  note: DescriptionOutlinedIcon,
}

export default function SearchSuggestionsDropdown({
  suggestions,
  searchValue,
  searchBy,
  selectedIndex,
  onSuggestionClicked,
  onSeeAllClicked,
}) {
  if (!suggestions.length) {
    return (
      <DropdownPaper elevation={0} role="listbox">
        <EmptyState>
          <EmptyIconWrap>
            <SearchOffOutlinedIcon fontSize="small" />
          </EmptyIconWrap>
          <Typography variant="body2" fontWeight={600}>
            No results found
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try a different name, email, or Search by type
          </Typography>
        </EmptyState>
      </DropdownPaper>
    )
  }

  return (
    <DropdownPaper elevation={0} role="listbox">
      <List disablePadding>
        {suggestions.map((item, index) => {
          const colors =
            SEARCH_TYPE_COLORS[item.type] || SEARCH_TYPE_COLORS.note
          const TypeIcon = TYPE_ICONS[item.type] || PersonOutlinedIcon
          return (
            <SuggestionRow
              key={`${item.type}-${item.id}`}
              role="option"
              selected={index === selectedIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSuggestionClicked(item)}
            >
              {item.type === "candidate" ? (
                <CandidateAvatar
                  candidate={item}
                  size="40px"
                  fontSize="14px"
                  rootSX={{ mr: 1.5, mt: 0.25, flexShrink: 0 }}
                />
              ) : (
                <TypeIconBadge $bg={colors.bg} $fg={colors.fg}>
                  <TypeIcon sx={{ fontSize: 18 }} />
                </TypeIconBadge>
              )}
              <Column sx={{ minWidth: 0, gap: 0.25, flex: 1 }}>
                <TypeChip $bg={colors.bg} $fg={colors.fg}>
                  {item.type_label ||
                    SEARCH_TYPE_LABELS[item.type] ||
                    item.type}
                </TypeChip>
                <Typography variant="body2" fontWeight={650} noWrap>
                  {highlightMatch(
                    item.title || item.candidate_name,
                    searchValue
                  )}
                </Typography>
                {item.type === "candidate" || item.type === "lead" ? (
                  <>
                    {item.email ? (
                      <MetaLine>
                        {highlightMatch(item.email, searchValue)}
                      </MetaLine>
                    ) : null}
                    {item.phone ? (
                      <MetaLine>
                        {highlightMatch(item.phone, searchValue)}
                      </MetaLine>
                    ) : null}
                    {item.company_name ? (
                      <MetaLine>
                        {highlightMatch(item.company_name, searchValue)}
                      </MetaLine>
                    ) : null}
                  </>
                ) : (
                  <>
                    {item.subtitle ? (
                      <MetaLine>
                        {highlightMatch(item.subtitle, searchValue)}
                      </MetaLine>
                    ) : null}
                    {item.detail ? (
                      <MetaLine>
                        {highlightMatch(item.detail, searchValue)}
                      </MetaLine>
                    ) : null}
                  </>
                )}
              </Column>
            </SuggestionRow>
          )
        })}
      </List>
      <Divider />
      <SeeAllRow
        onMouseDown={(event) => event.preventDefault()}
        onClick={onSeeAllClicked}
      >
        {SEARCH_BY_SEE_ALL_LABELS[searchBy] || SEARCH_BY_SEE_ALL_LABELS.all}
      </SeeAllRow>
    </DropdownPaper>
  )
}

const DropdownPaper = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "calc(100% + 10px)",
  left: 0,
  right: 0,
  zIndex: 20,
  maxHeight: 440,
  overflowY: "auto",
  padding: "6px !important",
  borderRadius: 18,
  border: `1px solid ${theme.palette.colors.brand200}`,
  background: "rgba(255, 255, 255, 0.96)",
  backdropFilter: "blur(18px)",
  boxShadow:
    "0 18px 40px -12px rgba(147, 32, 213, 0.22), 0 8px 16px -8px rgba(16, 24, 40, 0.12)",
  "&:before": {
    display: "none",
  },
  "& mark": {
    backgroundColor: theme.palette.colors.brand100,
    color: theme.palette.colors.brand700,
    fontWeight: 700,
    padding: "0 2px",
    borderRadius: 4,
  },
}))

const SuggestionRow = styled(ListItemButton)(({ theme }) => ({
  alignItems: "flex-start",
  gap: 0,
  padding: theme.spacing(1.25, 1.25),
  borderRadius: 12,
  marginBottom: 2,
  "&.Mui-selected": {
    backgroundColor: theme.palette.colors.brand50,
  },
  "&.Mui-selected:hover": {
    backgroundColor: theme.palette.colors.brand100,
  },
  "&:hover": {
    backgroundColor: theme.palette.colors.gray50,
  },
}))

const TypeIconBadge = styled("div")(({ $bg, $fg }) => ({
  width: 40,
  height: 40,
  borderRadius: 12,
  marginRight: 12,
  marginTop: 2,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: $bg,
  color: $fg,
}))

const TypeChip = styled("span")(({ $bg, $fg }) => ({
  alignSelf: "flex-start",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  lineHeight: 1.4,
  padding: "2px 7px",
  borderRadius: 999,
  backgroundColor: $bg,
  color: $fg,
}))

const MetaLine = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  lineHeight: 1.4,
  color: theme.palette.colors.gray600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}))

const EmptyState = styled(Column)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(4, 2),
  textAlign: "center",
}))

const EmptyIconWrap = styled("div")(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.colors.brand50,
  color: theme.palette.colors.brand600,
  marginBottom: theme.spacing(0.5),
}))

const SeeAllRow = styled("button")(({ theme }) => ({
  display: "flex",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  border: 0,
  background: "transparent",
  cursor: "pointer",
  padding: theme.spacing(1.25, 2),
  marginTop: 2,
  borderRadius: 12,
  fontSize: 13,
  fontWeight: 650,
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.colors.brand50,
  },
}))
